<script setup>
import { computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { destinationBySlug } from '@/data/content.js'

defineOptions({ layout: PublicLayout })

const props = defineProps({ slug: { type: String, default: '' } })
const d = computed(() => destinationBySlug(props.slug))
</script>

<template>
  <Head>
    <title>{{ d ? `${d.name} · Kenya2Go` : 'Destination · Kenya2Go' }}</title>
    <meta v-if="d" name="description" :content="d.blurb">
  </Head>

  <div v-if="d" class="kp">
    <header class="dhero" :style="{ '--h': d.hue }">
      <div class="dhero__art" aria-hidden="true"></div>
      <div class="dhero__inner">
        <a href="/destinations" class="dhero__back">← All destinations</a>
        <p class="dhero__region">{{ d.region }}</p>
        <h1 class="dhero__title">{{ d.name }}</h1>
        <p class="dhero__tag">{{ d.tag }}</p>
      </div>
    </header>

    <div class="dlayout">
      <div class="dmain">
        <div class="kp-prose">
          <p v-for="(para, i) in d.overview" :key="i">{{ para }}</p>
        </div>
      </div>
      <aside class="dside">
        <div class="dbox">
          <h3 class="dbox__h">Highlights</h3>
          <ul class="dbox__list">
            <li v-for="h in d.highlights" :key="h">{{ h }}</li>
          </ul>
        </div>
        <div class="dbox">
          <h3 class="dbox__h">When to go</h3>
          <p class="dbox__p">{{ d.bestTime }}</p>
        </div>
        <a href="/tours" class="kp-btn kp-btn--solid dcta">See experiences here</a>
      </aside>
    </div>

    <section class="kp-section dexp">
      <h2 class="kp-title">Experiences in {{ d.name }}</h2>
      <ul class="dexp__list">
        <li v-for="e in d.experiences" :key="e">
          <a href="/tours" class="dexp__item">
            <span>{{ e }}</span>
            <span class="dexp__arrow">→</span>
          </a>
        </li>
      </ul>
    </section>
  </div>

  <div v-else class="kp">
    <section class="kp-section kp-section--narrow dmiss">
      <h1 class="kp-title">We couldn’t find that one</h1>
      <p class="kp-prose">That destination isn’t live yet. <a href="/destinations" class="dmiss__link">Browse all destinations →</a></p>
    </section>
  </div>
</template>

<style scoped>
.dhero { position: relative; padding: 6rem 0 2.5rem; overflow: hidden; isolation: isolate; }
.dhero__art { position: absolute; inset: 0; z-index: -1;
  background:
    radial-gradient(120% 100% at 80% 0%, oklch(0.78 0.13 calc(var(--h))) 0%, transparent 55%),
    linear-gradient(150deg, oklch(0.58 0.15 calc(var(--h))) 0%, oklch(0.34 0.09 calc(var(--h) + 18)) 100%); }
.dhero__art::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, oklch(0.2 0.04 40 / 0.15), oklch(0.2 0.04 40 / 0.35)); }
.dhero__inner { max-width: 70rem; margin-inline: auto; padding-inline: 1.5rem; position: relative; }
.dhero__back { color: oklch(0.97 0.02 85 / 0.9); text-decoration: none; font-size: 0.85rem; font-weight: 500; }
.dhero__region { margin: 1.4rem 0 0.4rem; font-size: 0.72rem; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600; color: oklch(0.92 0.06 80); }
.dhero__title { font-family: var(--display); font-weight: 400; font-size: clamp(2.6rem, 7vw, 4.6rem); line-height: 0.98; letter-spacing: -0.02em; margin: 0; color: oklch(0.99 0.02 85); text-shadow: 0 2px 24px oklch(0.2 0.05 40 / 0.4); }
.dhero__tag { margin: 0.6rem 0 0; font-size: 1.05rem; color: oklch(0.96 0.03 82); }

.dlayout { max-width: 70rem; margin: 0 auto; padding: 3.5rem 1.5rem 1rem; display: grid; grid-template-columns: 1.6fr 1fr; gap: 3rem; }
.dside { display: flex; flex-direction: column; gap: 1.2rem; align-self: start; position: sticky; top: 1.5rem; }
.dbox { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-lg); padding: 1.3rem 1.4rem; }
.dbox__h { font-family: var(--display); font-weight: 500; font-size: 1.15rem; margin: 0 0 0.7rem; color: var(--ink); }
.dbox__list { margin: 0; padding-left: 1.1rem; color: var(--inkSoft); line-height: 1.7; font-size: 0.95rem; }
.dbox__p { margin: 0; color: var(--inkSoft); line-height: 1.6; font-size: 0.95rem; }
.dcta { width: 100%; }

.dexp__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.6rem; }
.dexp__item { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.2rem; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); text-decoration: none; color: var(--ink); font-weight: 500; transition: border-color .15s ease, transform .15s ease; }
.dexp__item:hover { border-color: var(--accent); transform: translateX(3px); }
.dexp__arrow { color: var(--accent); }
.dmiss { text-align: center; }
.dmiss__link { color: var(--accent); text-decoration: none; }

@media (max-width: 760px) {
  .dlayout { grid-template-columns: 1fr; gap: 2rem; }
  .dside { position: static; }
}
</style>
